import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  FormControl,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { SaveEmailAccount, UpdateEmailAccount } from "../../../services/EmailAccountsService";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../../common/CommonLoader";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const AddEmailAccount = ({ open, onClose, onSave, selectedAccount }) => {
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (selectedAccount) {
      setName(selectedAccount.name || "");
      setPassword(selectedAccount.password || "");
      setIsActive(
        selectedAccount.isActive !== undefined ? selectedAccount.isActive : true
      );
    } else {
      resetForm();
    }
  }, [selectedAccount]);

  const resetForm = () => {
    setName("");
    setPassword("");
    setIsActive(true);
    setShowPassword(false);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validate = () => {
    const newErrors = {};

    // Required field checks
    if (!name) {
      newErrors.name = "Email name is required";
    } else if (!validateEmail(name)) {
      newErrors.name = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Email length validation
    if (name && name.length > 200) {
      newErrors.name = "Email name must be maximum 200 characters";
    }

    // Password length validation
    if (password && password.length > 500) {
      newErrors.password = "Password must be maximum 500 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const accountData = {
        id: selectedAccount?.id || 0,
        name,
        password,
        isActive,
        isDeleted: selectedAccount?.isDeleted || false,
      };

      let response;
      if (selectedAccount) {
        setLoading(true);
        response = await UpdateEmailAccount(accountData);
        NotificationManager.success("Email account updated successfully");
      } else {
        setLoading(true);
        response = await SaveEmailAccount(accountData);
        NotificationManager.success("Email account saved successfully");
      }
      setLoading(false);
      if (response?.status === 200) {
        onSave();
        onClose();
      } else {
        NotificationManager.error("Error saving email account");
      }
    } catch (error) {
      NotificationManager.error("Error saving email account");
    }
  };

  const handleCancel = () => onClose();

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAccount ? "Update Email Account" : "Add New Email Account"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 200 }}
                placeholder="example@domain.com"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
                error={!!errors.password}
                helperText={errors.password}
                inputProps={{ maxLength: 500 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                margin="dense"
                variant="outlined"
                size="small"
              >
                <TextField
                  select
                  label="Status"
                  value={isActive}
                  size="small"
                  onChange={(e) => setIsActive(e.target.value === "true")}
                  SelectProps={{
                    native: true,
                  }}
                >
                  <option value={true}>Active</option>
                  <option value={false}>Inactive</option>
                </TextField>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedAccount ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddEmailAccount;